import { useEffect } from 'react'
import { Box } from '@mui/material'
import AppDatePicker, { type AppDatePickerProps } from './AppDatePicker'
import { type DateRangeValue } from './dateRange'
import { useRangeLimits } from './useRangeLimits'
import { useAppTheme } from '@/theme/ThemeContext'

export type AppDateRangePickerProps = {
  value: DateRangeValue
  onChange: (value: DateRangeValue) => void
  fromLabel?: string
  toLabel?: string
  /** Longest window, counting both end days. Longer days are disabled. */
  maxWindowDays?: number
  minDate?: Date
  maxDate?: Date
  disableFuture?: boolean
  clearable?: boolean
  withTime?: boolean
  disabled?: boolean
  width?: string
  /**
   * True while a field holds a date the limits refuse. `onChange` still gets
   * that date, so the caller must not search while this is true.
   */
  onErrorChange?: (hasError: boolean) => void
}

type SharedLimits = Pick<
  AppDatePickerProps,
  'disableFuture' | 'clearable' | 'withTime' | 'disabled' | 'width'
>

export default function AppDateRangePicker({
  value,
  onChange,
  fromLabel = 'מתאריך',
  toLabel = 'עד תאריך',
  maxWindowDays,
  minDate,
  maxDate,
  onErrorChange,
  ...shared
}: AppDateRangePickerProps & SharedLimits) {
  const { mode } = useAppTheme()
  const { from, to, hasError } = useRangeLimits(value, {
    minDate,
    maxDate,
    maxWindowDays,
  })
  useEffect(() => {
    onErrorChange?.(hasError)
    return () => onErrorChange?.(false)
  }, [hasError, onErrorChange])

  return (
    <Box
      dir="rtl"
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
    >
      <AppDatePicker
        {...shared}
        {...from}
        label={fromLabel}
        value={value.from}
        onChange={next => onChange({ ...value, from: next })}
      />
      <Box
        component="span"
        aria-hidden
        sx={{ color: mode === 'dark' ? '#BBD7FF' : '#003F61', px: 0.25 }}
      >
        —
      </Box>
      <AppDatePicker
        {...shared}
        {...to}
        label={toLabel}
        value={value.to}
        onChange={next => onChange({ ...value, to: next })}
      />
    </Box>
  )
}
